import * as React from 'react';
import { connect } from 'react-redux';
import { Button, Table } from 'reactstrap';
import { apiRequest } from '../../APIClient';
import { DEFAULT_ROUTE_PATH, ROUTE_PATH } from '../../constants';
import { sessionSelector } from '../../reducers/session';
import { RouteComponentProps } from 'react-router-dom';
import { Dispatch, SwitchableCustomerType } from '../../types';
import Icon from '../../widgets/Icons';
import Panel from '../../widgets/Panel';
import ViewHeader from '../../widgets/ViewHeader';
import Viewport from '../../widgets/Viewport';

type OwnProps = RouteComponentProps;

export type StoreProps = {
  username: string | null;
  currentCustomerId: string | null;
  customers: Array<SwitchableCustomerType>;
};

export type DispatchProps = {
  getCustomerNames: () => void;
  switchCustomer: (
    id: string,
    riskID: string | undefined,
    nextRoute: string,
  ) => void;
};

export type Props = OwnProps & StoreProps & DispatchProps;

class MSPCustomerSwitchView extends React.Component<Props> {
  componentDidMount() {
    this.props.getCustomerNames();
  }

  changeToDefaultRoute = () => {
    this.props.history.push(DEFAULT_ROUTE_PATH);
  };

  changeToCredentialRoute = () => {
    this.props.history.push(ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.MAIN);
  };

  changeToContactRoute = () => {
    this.props.history.push(ROUTE_PATH.CONTACT);
  };

  render() {
    const { customers, currentCustomerId, switchCustomer, username } =
      this.props;
    const calculateConfigScorePercentage = (customer: SwitchableCustomerType) =>
      // Configuration score from the backend is out of 35
      Math.floor((customer.configuration_score ?? 0) / 0.35) + '%';
    return (
      <Viewport>
        <ViewHeader title={`Welcome ${username || 'to Arctic Wolf'}`} />
        <Panel>
          <Table>
            <thead>
              <tr>
                <th>Switch To</th>
                <th className="text-center">Coverage Score</th>
                <th className="text-center">Configuration</th>
                <th className="text-center">Credential Management</th>
                <th className="text-center">Open A Ticket</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => {
                const isSelf = currentCustomerId === customer.id;
                return (
                  <tr key={customer.id} className={isSelf ? 'table-info' : ''}>
                    <td>
                      <Button
                        outline
                        color="primary"
                        onClick={() =>
                          isSelf
                            ? this.changeToDefaultRoute()
                            : switchCustomer(
                                customer.id,
                                customer.riskID,
                                DEFAULT_ROUTE_PATH,
                              )
                        }
                      >
                        {customer.name}
                      </Button>
                    </td>
                    <td className="text-center" data-cy="coverage-score">
                      {customer.security_score}%
                    </td>
                    <td className="text-center" data-cy="configuration">
                      {calculateConfigScorePercentage(customer)}
                    </td>
                    <td className="text-center">
                      <Button
                        outline
                        color="primary"
                        onClick={() =>
                          isSelf
                            ? this.changeToCredentialRoute()
                            : switchCustomer(
                                customer.id,
                                customer.riskID,
                                ROUTE_PATH.SELF_SERVICE.CONNECTED_ACCOUNTS.MAIN,
                              )
                        }
                      >
                        <Icon.Key className="cursor-pointer" />
                      </Button>
                    </td>
                    <td className="text-center">
                      <Button
                        outline
                        color="primary"
                        onClick={() =>
                          isSelf
                            ? this.changeToContactRoute()
                            : switchCustomer(
                                customer.id,
                                customer.riskID,
                                ROUTE_PATH.CONTACT,
                              )
                        }
                      >
                        <Icon.Envelope className="cursor-pointer" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Panel>
      </Viewport>
    );
  }
}

export default connect<StoreProps, DispatchProps, OwnProps>(
  (store): StoreProps => ({
    username: sessionSelector.getUsername(store),
    currentCustomerId: sessionSelector.getCustomerID(store),
    customers: sessionSelector.getSwitchableCustomers(store),
  }),
  (dispatch: Dispatch): DispatchProps => ({
    getCustomerNames: () => dispatch(apiRequest.getMspEndCustomerNames()),
    switchCustomer: (
      id: string,
      riskID: string | undefined = undefined,
      nextRoute: string,
    ) => dispatch(apiRequest.switchMSPCustomer(id, riskID, nextRoute)),
  }),
)(MSPCustomerSwitchView);
